import type { DropdownV2 } from "../dropdown/dropdown-v2";

export class SelectV2 extends HTMLElement {
  private select: HTMLSelectElement;
  private valueNode: HTMLElement | undefined = undefined;
  private valueNodeDefaultText: string | undefined = undefined;
  private menuItems: HTMLElement[];
  private dropdown: DropdownV2 | null;
  private defaultSelectedOptions: HTMLOptionElement[];
  private form: HTMLFormElement | null;

  private controller?: AbortController;

  constructor() {
    super();

    const select = Array.from(this.children).find(
      (children) => children instanceof HTMLSelectElement,
    );

    if (select === undefined) {
      throw new Error(
        "SelectV2Error: No select element found in select-v2 Element",
      );
    }

    this.select = select;
    this.defaultSelectedOptions = Array.from(select.selectedOptions);

    const valueNodeSelector = this.getAttribute("data-value-selector");

    if (valueNodeSelector !== null) {
      this.valueNode = Array.from(this.children)
        .filter(
          (children) => children.tagName.toLowerCase() !== "dropdown-v2-menu",
        )
        .map((children) =>
          children.querySelector<HTMLElement>(valueNodeSelector),
        )
        .find((node) => node !== null);
      this.valueNodeDefaultText = this.valueNode?.innerHTML;
    }

    this.menuItems = Array.from(
      this.querySelectorAll<HTMLElement>("[data-option]"),
    );

    this.dropdown = this.querySelector<DropdownV2>("dropdown-v2");

    this.form = this.closest("form");
  }

  connectedCallback() {
    this.controller = new AbortController();

    this.select.addEventListener(
      "change",
      () => {
        const selectedOptions = Array.from(this.select.selectedOptions);

        this.menuItems.forEach((item, index) => {
          item.classList.toggle(
            "active",
            selectedOptions.some((option) => option.index === index),
          );
        });

        if (this.valueNode !== undefined) {
          if (
            selectedOptions.length === 0 &&
            this.valueNodeDefaultText !== undefined
          ) {
            this.valueNode.innerHTML = this.valueNodeDefaultText;
          } else {
            this.valueNode.innerHTML = selectedOptions
              .map((option) => option.innerHTML)
              .join(", ");
          }
        }
      },
      {
        signal: this.controller?.signal,
      },
    );

    this.menuItems.forEach((item, index) => {
      item.addEventListener(
        "click",
        () => {
          if (this.select.multiple === true) {
            Array.from(this.select.options).forEach((option) => {
              if (option.index === index) {
                option.selected = !option.selected;
              }
            });
          } else {
            Array.from(this.select.options).forEach((option) => {
              option.selected = option.index === index;
            });

            this.dropdown?.setOpen(false);
          }

          this.dispatchSelectChangeEvent();
        },
        {
          signal: this.controller?.signal,
        },
      );
    });

    this.form?.addEventListener(
      "reset",
      () => {
        Array.from(this.select.selectedOptions).forEach((option) => {
          option.selected = this.defaultSelectedOptions.includes(option);
        });

        this.dispatchSelectChangeEvent();
      },
      {
        signal: this.controller?.signal,
      },
    );
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  private dispatchSelectChangeEvent = () => {
    this.select.dispatchEvent(
      new Event("change", {
        bubbles: true,
        cancelable: true,
      }),
    );
  };
}
